import { useEffect, useRef, useState } from 'react'
import { CalendarClock, CheckCircle2, ListChecks } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { toast } from 'sonner'
import api from '@/services/api'
import type { Deal, DealStage } from '@/types'
import { mapDealFromApi, stagePatchPayload } from '@/adapters/deals'
import { cn } from '@/utils/cn'
import { useDealsStore } from '@/stores/useDealsStore'
import { useAuthStore } from '@/stores/authStore'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { DatetimeLocalField } from './DatetimeLocalField'
import {
  toDatetimeLocalValue,
  isValidReuniaoId,
  extractReuniaoFromApiResponse,
} from './reuniaoMeetingUtils'
import {
  agendarFieldIconWrapClass,
  agendarFieldLabelClass,
  agendarFieldShellClass,
} from './agendarReuniaoFieldStyles'

export const AGENDAR_REUNIAO_SECTION_ID = 'agendar-reuniao-section'

function resolveCloserId(deal: Deal): string | null {
  const fromDeal = deal.closerId?.trim()
  if (isValidReuniaoId(fromDeal)) return fromDeal

  const fromUser = useAuthStore.getState().user?.id?.trim()
  if (isValidReuniaoId(fromUser)) return fromUser

  return null
}

export function AgendarReuniaoSection({
  deal,
  onDealPatched,
  highlightMeetingDate = false,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
  highlightMeetingDate?: boolean
}) {
  const clearDealPanelHighlight = useDealsStore((s) => s.clearDealPanelHighlight)
  const sectionRef = useRef<HTMLDivElement>(null)
  const [dataHora, setDataHora] = useState(() => toDatetimeLocalValue(deal.dataReuniao))
  const [tipo, setTipo] = useState('Descoberta')
  const [scheduling, setScheduling] = useState(false)

  useEffect(() => {
    setDataHora(toDatetimeLocalValue(deal.dataReuniao))
  }, [deal.id, deal.dataReuniao])

  useEffect(() => {
    if (!highlightMeetingDate) return
    const section = sectionRef.current
    if (!section) return
    const frame = requestAnimationFrame(() => {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => cancelAnimationFrame(frame)
  }, [deal.id, highlightMeetingDate])

  const handleConfirmar = async () => {
    if (!dataHora) {
      toast.error('Selecione a data e a hora da reunião antes de avançar para Reunião Agendada.')
      return
    }

    const closerId = resolveCloserId(deal)
    if (!closerId) {
      toast.error('Associe um closer responsável ao deal antes de agendar a reunião.')
      return
    }

    setScheduling(true)
    try {
      if (!isValidReuniaoId(deal.closerId?.trim())) {
        await api.patch(`/deals/${deal.id}`, { closerId })
      }

      const { data: reuniaoData } = await api.post('/reunioes', {
        dealId: deal.id,
        closerId,
        data: new Date(dataHora).toISOString(),
        tipo,
        status: 'Agendada',
      })
      const created = extractReuniaoFromApiResponse(reuniaoData)
      if (!created?.id) {
        throw new Error('reuniao_create_failed')
      }
      const { data: stageData } = await api.patch(
        `/deals/${deal.id}/stage`,
        stagePatchPayload('reuniao_agendada' as DealStage),
      )
      await api.patch(`/deals/${deal.id}`, { dataReuniao: new Date(dataHora).toISOString() } as Partial<Deal>)
      onDealPatched({
        ...mapDealFromApi(stageData.data ?? stageData),
        closerId,
        dataReuniao: dataHora,
        reunioes: [
          {
            id: created.id,
            data:
              typeof created.data === 'string'
                ? created.data
                : new Date(dataHora).toISOString(),
            tipo: created.tipo ?? tipo,
            status: created.status ?? 'Agendada',
            closerId: created.closerId ?? deal.closerId,
            closerNome: created.closerNome ?? deal.closerNome,
          },
        ],
      })
      clearDealPanelHighlight(deal.id)
      toast.success('Reunião agendada com sucesso!')
    } catch (error) {
      toast.error(getFriendlyError(error, 'Erro ao agendar reunião'))
    } finally {
      setScheduling(false)
    }
  }

  return (
    <div
      id={AGENDAR_REUNIAO_SECTION_ID}
      ref={sectionRef}
      className={cn(
        'deal-detail-hero-card glass-card scroll-mt-4 space-y-4 p-4',
        highlightMeetingDate && 'border-l-warning',
      )}
    >
      <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent">
        <CalendarClock size={14} />
        Agendar Reunião
      </h3>

      {highlightMeetingDate && (
        <p className="rounded-md border border-warning px-3 py-2 text-[11px] font-medium leading-snug text-text-secondary">
          Preencha a data e a hora da reunião para mover este deal para Reunião Agendada. Datas
          passadas são permitidas para registrar reuniões já realizadas.
        </p>
      )}

      <div className="space-y-3">
        <DatetimeLocalField
          value={dataHora}
          attention
          highlight={highlightMeetingDate}
          openOnMount={highlightMeetingDate}
          aria-invalid={highlightMeetingDate}
          placeholder="Toque em qualquer lugar para escolher data e hora"
          onChange={(next) => {
            setDataHora(next)
            if (next) clearDealPanelHighlight(deal.id)
          }}
        />

        <div className={cn('flex items-center gap-3', agendarFieldShellClass)}>
          <span className={agendarFieldIconWrapClass}>
            <ListChecks size={18} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <label
              htmlFor="agendar-reuniao-tipo"
              className={agendarFieldLabelClass}
            >
              Tipo de reunião
            </label>
            <select
              id="agendar-reuniao-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="mt-0.5 w-full cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-text-primary outline-none focus:ring-0"
            >
              <option value="Descoberta">Descoberta</option>
              <option value="Apresentação de Proposta">Apresentação de Proposta</option>
              <option value="Validação">Validação</option>
              <option value="Follow-up">Follow-up</option>
            </select>
          </div>
        </div>
      </div>

      <Button
        variant="primary"
        size="sm"
        icon={<CheckCircle2 size={13} />}
        loading={scheduling}
        onClick={handleConfirmar}
        className="w-full"
      >
        Confirmar Agendamento
      </Button>
    </div>
  )
}
